import { useCallback, useEffect, useRef } from 'react';
import { useImmer } from 'use-immer';
import globalWindow from '@shared/core/globals';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import { DEFAULT_OFFSET } from './Popover.constants';
import type { PopoverInternalState, PopoverV2Props, OnPopperUpdateHandler, PopoverTimers } from './Popover.types';
import { callAllHandlers } from '@shared/utils/functions';
import { getValues } from '@shared/utils/object';
import { isTriggerClick, isTriggerHover } from './Popover.utils';
import type { BoxProps } from '../Box';
import { useIds, useDisclosure, useShouldKeepOverlayOpen } from '@withjoy/joykit/hooks';
import { mergeRefs } from '@shared/utils/hooks/setRef';
import type { HTMLProps, PropGetter } from '@withjoy/joykit/utils';
import { isEscapeKeyClick } from '@withjoy/joykit/utils';
import { contains } from '@shared/utils/dom';
import { noop } from '@shared/utils/noop';
import { withWindow } from '@shared/utils/withWindow';
import { useOnShowFocus, useOnHideFocus } from './useFocus';
import { usePopper } from './usePopper';
import { claimTooltipSession, isTooltipSessionWarm, releaseTooltipSession } from './hoverTooltipCoordinator.internal';

/**
 * https://developer.mozilla.org/en-US/docs/Web/API/FocusEvent/relatedTarget
 *
 * - `blur` event -> the `EventTarget` receiving focus (if any)
 * - `focus` event -> the `EventTarget` losing focus (if any)
 */
const getRelatedTarget = (e: Pick<FocusEvent, 'relatedTarget' | 'target' | 'currentTarget'>) => {
  const target = (e.target ?? e.currentTarget) as HTMLElement;

  return (e.relatedTarget ?? target?.ownerDocument?.activeElement) as HTMLElement;
};

export type UsePopoverArgs = Omit<PopoverV2Props, 'children'> & Required<Pick<PopoverV2Props, 'placement' | 'triggerType'>>;

export const usePopover = (props: UsePopoverArgs) => {
  const {
    closeOnOutsideClick,
    disableCloseOnBlur,
    disableCloseOnEscapeKeyClick,
    disableCloseOnOutsideClick,
    disableDisclosureAria,
    isOpen: isOpenProp,
    onClose: onCloseProp,
    onMouseEnterDelay,
    onMouseLeaveDelay = 300,
    onOpen: onOpenProp,
    placement: placementProp,
    popperOptions,
    triggerType,
    coordinateHover,
  } = props;

  const { isOpen, onOpen, onClose, onToggle } = useDisclosure({ isOpen: isOpenProp, onOpen: onOpenProp, onClose: onCloseProp });
  // Autogenerate IDs to facilitate WAI-ARIA and server rendering.
  const [popoverId] = useIds(props.id, 'joykit-popover');
  const popperRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLElement>(null);
  const arrowRef = useRef<HTMLElement>(null);
  const tetherReferenceRef = useRef<HTMLElement | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<PopoverTimers>({});
  const isHoveringRef = useRef<boolean>(false);
  const isDismissedRef = useRef(false);
  const coordinated = coordinateHover && isTriggerHover(triggerType);

  const cancelHoverTimers = useEventCallback(() => {
    globalWindow.clearTimeout(timersRef.current.onMouseEnter);
    globalWindow.clearTimeout(timersRef.current.onMouseLeave);
  });
  const dismissTooltip = useEventCallback(() => {
    cancelHoverTimers();
    isDismissedRef.current = true;
    onClose();
  });

  useIsomorphicLayoutEffect(() => {
    if (!coordinated || !isOpen) return;
    const document = triggerRef.current?.ownerDocument;
    if (!document) return;
    claimTooltipSession(document, dismissTooltip);
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || disableCloseOnEscapeKeyClick) return;
      // A pointer-only tooltip must not consume Escape intended for another focused control.
      const focused = document.activeElement as HTMLElement | null;
      if (contains(triggerRef.current, focused) || contains(popperRef.current, focused)) {
        event.preventDefault();
        event.stopPropagation();
      }
      dismissTooltip();
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => {
      document.removeEventListener('keydown', handleEscape, true);
      releaseTooltipSession(document, dismissTooltip);
    };
  }, [coordinated, isOpen, dismissTooltip, disableCloseOnEscapeKeyClick]);

  const [state, setState] = useImmer<PopoverInternalState>({
    arrowOffset: DEFAULT_OFFSET,
    placement: placementProp,
    transformOrigin: undefined,
  });
  const { arrowOffset, placement: placementState, transformOrigin } = state;

  useOnHideFocus(popperRef, { isVisible: isOpen, shouldFocus: triggerType === 'click', focusRef: triggerRef });
  useOnShowFocus(popperRef, { isVisible: isOpen, shouldFocus: triggerType === 'click' });

  const handleOnPopperUpdate = useEventCallback<OnPopperUpdateHandler>((placement, offsets, transformOrigin) => {
    timersRef.current.onPopperUpdate = globalWindow.setTimeout(() => {
      setState(draft => {
        draft.placement = placement;
        draft.transformOrigin = transformOrigin || undefined;
      });
    });
  });

  usePopper({
    anchorRef: tetherReferenceRef.current,
    popperRef: popperRef.current,
    arrowRef: arrowRef.current,
    placement: placementProp,
    onPopperUpdate: handleOnPopperUpdate,
    options: popperOptions,
  });

  const { shouldKeepOverlayOpen, setVisibilityForKey } = useShouldKeepOverlayOpen({ refValues: { content: false } });

  ////////////////////////
  // Cleanup

  useEffect(() => {
    return () => {
      // Clear any existing timers to avoid memory-leaks + actions occuring when component is unmounted
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const { ...timers } = timersRef.current;
      getValues(timers).forEach(timerId => {
        globalWindow.clearTimeout(timerId);
      });
    };
  }, []);

  ////////////////////////
  // Outside-click dismissal

  // Opt-in (see `closeOnOutsideClick`). `onBlur` above only fires once focus has actually moved, so
  // it misses presses on surfaces that never take focus — an explicit pointer listener catches
  // those. It does *not* catch presses inside a cross-origin `<iframe>`: those are dispatched in the
  // frame's own browsing context and never reach this window. Focusing the frame does blur whatever
  // held focus in this document, so the blur path above is what closes the popover in that case.
  //
  // `pointerdown` rather than `click` so the popover closes on press and never depends on a
  // delegated `click` reaching the window — iOS Safari withholds those from non-interactive
  // elements. Capture phase so that an outside surface which stops propagation on its own
  // `pointerdown` (`EditablePage`, `InlineEditorToolbar`) can't starve dismissal; presses that
  // belong to the popover are filtered by the `contains` guard below rather than by propagation.
  const shouldCloseOnOutsideClick = !!closeOnOutsideClick && !disableCloseOnOutsideClick;

  useEffect(() => {
    if (!isOpen || !shouldCloseOnOutsideClick) {
      return;
    }

    // Typed explicitly: the single-argument `withWindow` overload is `WindowWrapper<void>`, which
    // would swallow the cleanup's type and let a refactor drop it without a compiler error. `() =>
    // void` rather than `(() => void) | undefined` — `void` is assignable to the latter, so the
    // union checks nothing. Server-side there is no listener to detach, hence the `noop` default.
    return withWindow<() => void>(w => {
      const handleOutsidePointerDown = (event: Event) => {
        const target = event.target as HTMLElement | null;
        if (!target) {
          return;
        }

        // The trigger owns its own toggle; closing here too would fight it back open.
        if (contains(popperRef.current, target) || contains(triggerRef.current, target) || contains(anchorRef.current, target)) {
          return;
        }

        onClose();
      };

      w.addEventListener('pointerdown', handleOutsidePointerDown, true);
      return () => w.removeEventListener('pointerdown', handleOutsidePointerDown, true);
    }, noop);
  }, [isOpen, shouldCloseOnOutsideClick, onClose]);

  const prepareToOpen = useEventCallback<React.MouseEventHandler>(e => {
    isHoveringRef.current = true;
    isDismissedRef.current = false;
    const timers = timersRef.current;
    // Clear the `onMouseLeave` timer so that a user can move from anchor -> popover
    if (timers.onMouseLeave) {
      globalWindow.clearTimeout(timers.onMouseLeave);
    }

    globalWindow.clearTimeout(timers.onMouseEnter);
    const delay = coordinated && isTooltipSessionWarm(e.currentTarget.ownerDocument) ? 0 : onMouseEnterDelay;
    if (coordinated && delay === 0) {
      onOpen();
    } else {
      timers.onMouseEnter = globalWindow.setTimeout(() => {
        if (!coordinated || !isDismissedRef.current) onOpen();
      }, delay);
    }
  });
  const prepareToClose = useEventCallback<React.MouseEventHandler>(e => {
    isHoveringRef.current = false;
    const timers = timersRef.current;
    // Clear the `onMouseEnter` timer to prevent a stuck open state
    if (timers.onMouseEnter) {
      globalWindow.clearTimeout(timers.onMouseEnter);
    }

    globalWindow.clearTimeout(timers.onMouseLeave);
    timers.onMouseLeave = globalWindow.setTimeout(() => {
      const focused = (triggerRef.current?.ownerDocument.activeElement ?? null) as HTMLElement | null;
      const hasFocus = coordinated && (contains(triggerRef.current, focused) || contains(popperRef.current, focused));
      if (!isHoveringRef.current && !hasFocus) {
        onClose();
      }
    }, onMouseLeaveDelay);
  });

  ////////////////////////
  // Prop Getters

  const getAnchorProps: PropGetter = useCallback((props = {}, ref) => {
    return {
      ...props,
      ref: mergeRefs(ref, anchorRef, tetherReferenceRef),
    };
  }, []);

  const getTriggerProps: PropGetter = useCallback(
    (props = {}, ref) => {
      const triggerProps: HTMLProps = {
        ...props,
        ...(disableDisclosureAria
          ? // A description, not a disclosure: point at the content while it's showing so a
            // screen reader actually announces it. Only while open — otherwise the reference
            // resolves to an empty element. Appended to any caller-supplied description
            // (aria-describedby is a token list) so wrapping a field that already points at
            // its own error text does not drop that association.
            { 'aria-describedby': [props['aria-describedby'], isOpen ? popoverId : undefined].filter(Boolean).join(' ') || undefined }
          : {
              'aria-haspopup': props['aria-haspopup'] ?? 'true',
              'aria-expanded': isOpen ? 'true' : 'false',
              'aria-controls': isOpen ? popoverId : undefined,
            }),
        ref: mergeRefs(ref, triggerRef, (node: HTMLElement) => {
          if (!anchorRef.current) {
            tetherReferenceRef.current = node;
          }
        }),
      };

      if (isTriggerHover(triggerType)) {
        triggerProps.onFocus = callAllHandlers(props.onFocus, () => {
          isDismissedRef.current = false;
          if (coordinated) cancelHoverTimers();
          onOpen();
        });
        triggerProps.onBlur = callAllHandlers(props.onBlur, e => {
          const maybeRelatedTarget = getRelatedTarget(e);

          if (!disableCloseOnBlur && !contains(popperRef.current, maybeRelatedTarget) && !(coordinated && isHoveringRef.current)) {
            onClose();
          }
        });
        triggerProps.onMouseEnter = callAllHandlers(props.onMouseEnter, prepareToOpen);
        triggerProps.onMouseLeave = callAllHandlers(props.onMouseLeave, prepareToClose);
      } else if (isTriggerClick(triggerType)) {
        triggerProps.onClick = callAllHandlers(props.onClick, () => {
          isOpen || shouldKeepOverlayOpen ? onClose() : onOpen();
        });
      }

      return triggerProps;
    },
    [
      shouldKeepOverlayOpen,
      disableCloseOnBlur,
      disableDisclosureAria,
      isOpen,
      onOpen,
      onClose,
      popoverId,
      prepareToOpen,
      prepareToClose,
      onToggle,
      triggerType,
      coordinated,
      cancelHoverTimers,
    ],
  );

  const getArrowProps: PropGetter = useCallback((props, ref) => {
    return {
      ...props,
      ref: mergeRefs(ref, arrowRef),
    };
  }, []);

  const getPopoverProps: PropGetter<HTMLDivElement, BoxProps> = useCallback(
    (props = {}, ref) => {
      const popoverProps: HTMLProps = {
        ...props,
        ref: mergeRefs(ref, popperRef),
        id: popoverId,
        // `dialog` is the right default for a popover that owns its own surface, but it is wrong
        // for a popup that already carries its own semantics — a combobox listbox (`SelectV1`)
        // announces as a dialog wrapping a listbox otherwise. Let a caller-supplied `role` win.
        role: props.role ?? 'dialog',
        tabIndex: -1,
        children: isOpen || shouldKeepOverlayOpen ? props.children : null,
        onKeyDown: callAllHandlers(props.onKeyDown, e => {
          if (!disableCloseOnEscapeKeyClick && isEscapeKeyClick(e.key)) {
            onClose();
          }
        }),
        onBlur: callAllHandlers(props.onBlur, e => {
          const maybeRelatedTarget = getRelatedTarget(e);
          const isContainedToContent = contains(popperRef.current, maybeRelatedTarget);
          const isContainedToTrigger = contains(triggerRef.current, maybeRelatedTarget);

          const isOutsideBlur = !isContainedToContent && !isContainedToTrigger;
          if (!disableCloseOnBlur && isOutsideBlur && !(coordinated && isHoveringRef.current)) {
            onClose();
          }
        }),
      };

      if (isTriggerHover(triggerType)) {
        popoverProps.role = props.role ?? 'tooltip';
        // The `hover` triggerType should consider both anchor + body
        popoverProps.onMouseEnter = callAllHandlers(props.onMouseEnter, () => {
          isHoveringRef.current = true;
          if (coordinated) globalWindow.clearTimeout(timersRef.current.onMouseLeave);
        });
        popoverProps.onMouseLeave = callAllHandlers(props.onMouseLeave, e => {
          isHoveringRef.current = false;
          prepareToClose(e);
        });
      }

      return popoverProps;
    },
    [disableCloseOnBlur, disableCloseOnEscapeKeyClick, isOpen, onClose, popoverId, prepareToClose, shouldKeepOverlayOpen, triggerType, coordinated],
  );

  return {
    arrowOffset,
    isOpen,
    onClose,
    placement: placementState,
    shouldKeepOverlayOpen,
    transformOrigin,

    getPopoverProps,
    getAnchorProps,
    getArrowProps,
    getTriggerProps,
    setVisibilityForKey,
  };
};
